import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import { resolveSessionInactivityPolicy } from '@/lib/auth/session-inactivity'
import { createLogger } from '@/lib/logger'
import { useOrganizationStore } from '@/store/organization.store'
import { LockKeyIcon } from '@phosphor-icons/react'
import { useLocation } from 'react-router-dom'

import { useAuth } from './AuthProvider'

const logger = createLogger('components/auth/SessionInactivityManager')
const ACTIVITY_WRITE_INTERVAL_MS = 5_000

export function SessionInactivityManager() {
  const { isAuthenticated, logout, user } = useAuth()
  const location = useLocation()
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const { data } = useOrganizationSettingsQuery({
    enabled: isAuthenticated && !!organizationId,
  })
  const policy = resolveSessionInactivityPolicy(data?.settings)
  const [locked, setLocked] = useState(false)
  const [logoutFailed, setLogoutFailed] = useState(false)
  const logoutInFlightRef = useRef(false)
  const logoutRef = useRef(logout)
  const returnPathRef = useRef('/')

  useEffect(() => {
    logoutRef.current = logout
  }, [logout])

  useEffect(() => {
    returnPathRef.current = `${location.pathname}${location.search}${location.hash}`
  }, [location.hash, location.pathname, location.search])

  const finishExpiredSession = useCallback(async () => {
    if (logoutInFlightRef.current) return
    logoutInFlightRef.current = true
    setLocked(true)
    setLogoutFailed(false)
    try {
      const redirect = `/login?redirect=${encodeURIComponent(
        returnPathRef.current,
      )}&reason=inactivity`
      await logoutRef.current({ redirectTo: redirect })
    } catch (error) {
      logger.error('Unable to confirm inactivity sign-out', error)
      setLogoutFailed(true)
      logoutInFlightRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !organizationId || !policy.enabled) {
      setLocked(false)
      setLogoutFailed(false)
      logoutInFlightRef.current = false
      return
    }

    const activityKey = `scrambl:session-activity:${user.id}:${organizationId}`
    let lastActivityAt = Date.now()
    let lastWrittenAt = 0
    let timerId: number | null = null

    const schedule = () => {
      if (timerId !== null) window.clearTimeout(timerId)
      const remaining = Math.max(
        0,
        lastActivityAt + policy.timeoutMs - Date.now(),
      )
      timerId = window.setTimeout(() => {
        if (Date.now() - lastActivityAt >= policy.timeoutMs) {
          void finishExpiredSession()
        } else {
          schedule()
        }
      }, remaining)
    }

    const recordActivity = () => {
      if (logoutInFlightRef.current) return
      const now = Date.now()
      if (now - lastActivityAt < 1_000) return
      lastActivityAt = now
      if (now - lastWrittenAt >= ACTIVITY_WRITE_INTERVAL_MS) {
        lastWrittenAt = now
        localStorage.setItem(activityKey, String(now))
      }
      schedule()
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== activityKey || !event.newValue) return
      const timestamp = Number(event.newValue)
      if (!Number.isFinite(timestamp) || timestamp <= lastActivityAt) return
      lastActivityAt = timestamp
      schedule()
    }

    // Starting or restoring an authenticated tenant session is activity. This
    // also prevents a timestamp left by a previous login from ending the new
    // session immediately.
    localStorage.setItem(activityKey, String(lastActivityAt))
    lastWrittenAt = lastActivityAt
    schedule()

    const events: Array<keyof WindowEventMap> = [
      'keydown',
      'pointerdown',
      'pointermove',
      'scroll',
      'touchstart',
      'wheel',
    ]
    events.forEach(event =>
      window.addEventListener(event, recordActivity, { passive: true }),
    )
    window.addEventListener('storage', handleStorage)

    return () => {
      if (timerId !== null) window.clearTimeout(timerId)
      events.forEach(event => window.removeEventListener(event, recordActivity))
      window.removeEventListener('storage', handleStorage)
    }
  }, [
    finishExpiredSession,
    isAuthenticated,
    organizationId,
    policy.enabled,
    policy.timeoutMs,
    user?.id,
  ])

  if (!locked) return null

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-background px-6"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="inactive-session-title"
    >
      <div className="w-full max-w-md space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          <LockKeyIcon className="h-7 w-7" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h1 id="inactive-session-title" className="text-xl font-semibold">
            Session ended after inactivity
          </h1>
          <p className="text-sm text-muted-foreground">
            This organization uses HIPAA session controls. Sign in again to
            continue where you left off.
          </p>
        </div>
        {logoutFailed && (
          <div className="space-y-3">
            <p className="text-sm text-destructive">
              Scrambl could not confirm the sign-out. The workspace remains
              locked.
            </p>
            <Button onClick={() => void finishExpiredSession()}>
              Retry sign out
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
